// FILE: src/components/ExamSelector.js
// Landing screen ("HomePage") — refactored to the design system in
// HomePage.css.  Sections, no nested wrappers:
//   1. Hero — brand wordmark + avatar, two-line headline, lede
//   2. Platform badge row — rotating Platform/Feature badges (PlatformBadgeRow)
//   3. Certifications — grid of cert cards (the page's only routing CTAs), each
//      carrying its product/telemetry badges via the canonical badge system
//   4. Footer — Bryan's external work links
//
// Behavior preserved verbatim:
//   • `onSelectExam(examId)` prop fires on cert-card click.
//   • `data-testid="exam-button-${examId}"` per card so App.test.js + the
//     QuestionEditor admin-flow tests continue to find them.
//   • Coming-soon certs (PMI-ACP) stay disabled/greyed; the "Coming Soon"
//     indicator is now an emblem assigned via the Badge Manager rather than a
//     hardcoded inline pill.
//   • Character image falls back to a glyph if the PNG 404s.
//   • Card list comes from getAllExams() — adding a new cert in
//     src/config/exams.js automatically gains a card here.
//
// Badge system (replaces the old per-exam ctaBadge + CertificationBadge):
//   • Platform/Feature badges render in PlatformBadgeRow above the grid.
//   • Each card asks badgeService.selectCardBadges() for its product (top-right)
//     and earned telemetry (top-left) badges, enforcing the max-badge rules.
//   • Telemetry is data-driven: pass `telemetryByCert` (examId → snapshot) to
//     light up social-proof badges. With no snapshot (today's default) none
//     render — social proof is EARNED, never hardcoded.

import React from 'react';
import { getAllExams } from '../../config/exams';
import { BADGE_PLACEMENT } from '../../config/badges';
import { selectCardBadges } from '../../services/badgeService';
import { getCertEmblems } from '../../services/badgeAdminService';
import CoachAvatar from '../coach/CoachAvatar';
import BadgeCallout from '../achievements/BadgeCallout';
import PlatformBadgeRow from '../achievements/PlatformBadgeRow';
import TestimonialsCarousel from '../testimonials/TestimonialsCarousel';
import { getPublicTestimonials } from '../../services/testimonialsService';
import './HomePage.css';

// ── External links (footer "Bryan's work" band) ──────────────────────────────

// localStorage hint: '1' when this browser last saw eligible testimonials —
// picks the home page's initial layout so neither direction flashes.
const TESTIMONIALS_HINT_KEY = 'pmPixel.hasTestimonials';

const BOOK_URL    = 'https://www.amazon.com/dp/B0DQ8Q34PV?ref_=pe_93986420_774957520';
const SEVENCS_URL = 'https://www.7c-consulting.com';
const LINKEDIN_URL = 'https://www.linkedin.com/in/bryancampbell/';

// ── Per-exam icon glyph + the icon-color class suffix used in the CSS ────────
//
// HomePage.css defines `.cert-icon.pmp`, `.cert-icon.cpmai`, `.cert-icon.acp`.
// Map our canonical examId → the CSS class suffix.
//
// When a logo file exists at `logoSrc`, render the image; otherwise fall back to
// a monospace text badge inside the existing tinted tile. Drop the official
// logos as PNGs in public/assets/cert-logos/ and they light up automatically —
// no JSX change required.
const EXAM_VISUALS = {
  pmp:       { label: 'PMP',   cssClass: 'pmp',   logoSrc: '/assets/cert-logos/pmp.png'   },
  cpmai:     { label: 'CPMAI', cssClass: 'cpmai', logoSrc: '/assets/cert-logos/cpmai.png' },
  'pmi-acp': { label: 'ACP',   cssClass: 'acp',   logoSrc: '/assets/cert-logos/acp.png'   },
  'pmi-pmocp': { label: 'PMOCP', cssClass: 'pmocp', logoSrc: '/assets/cert-logos/pmocp.png' },
};

// ── Reset the few <button> defaults the design relies on overriding ──────────
const buttonReset = {
  font:        'inherit',
  textAlign:   'left',
  appearance:  'none',
  WebkitAppearance: 'none',
};

// ── CertIcon — image-first, text-fallback badge ─────────────────────────────
//
// Tries the licensed logo PNG.  If it 404s (file not yet committed), swaps
// to a monospace cert-code on the tinted tile so the card always renders
// something sensible instead of a broken-image glyph.

function CertIcon({ visuals }) {
  const [logoFailed, setLogoFailed] = React.useState(false);
  const showLogo = visuals.logoSrc && !logoFailed;
  return (
    <div
      className={`cert-icon ${visuals.cssClass} ${showLogo ? 'has-logo' : ''}`}
      aria-hidden="true"
    >
      {showLogo ? (
        <img
          src={visuals.logoSrc}
          alt=""
          className="cert-logo"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <span className="cert-icon-label">{visuals.label}</span>
      )}
    </div>
  );
}

// ── CardBadges — product + telemetry capsules for one card ───────────────────
//
// Pulls the resolved badges from badgeService (which enforces ≤1 product, ≤1
// telemetry and the dual/single mode) and pins each to its corner. In 'single'
// mode both winners share the top-right slot; in 'dual' mode product is
// top-right and telemetry top-left.

function CardBadges({ certId, telemetryByCert, productOverride }) {
  const { product, telemetry } = selectCardBadges(certId, { telemetryByCert, productOverride });

  // Placement is purely type-driven (product → angled emblem sticker, telemetry
  // → top-right corner). The dual/single mode only affects WHICH badges show.
  const placementFor = (badge) => BADGE_PLACEMENT[badge.type] || 'top-right';

  return (
    <>
      {product && (
        <span className={`cert-badge-slot ${placementFor(product)}`}>
          <BadgeCallout badge={product} animate="fade" />
        </span>
      )}
      {telemetry && (
        <span className={`cert-badge-slot ${placementFor(telemetry)}`}>
          <BadgeCallout badge={telemetry} animate="pulse" />
        </span>
      )}
    </>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * @param {function} onSelectExam — called with an examId string when the
 *                                   learner clicks one of the cert cards.
 * @param {object}   [telemetryByCert={}] — map examId → telemetry snapshot used
 *                                   to determine which telemetry/social-proof
 *                                   badges are earned. Default {} → none show.
 */
export default function ExamSelector({ onSelectExam, telemetryByCert = {}, onOpenFoundersWall, journeySlot = null }) {
  const exams = getAllExams();

  // Admin-managed emblem assignments (migration 027). State machine:
  //   undefined → still loading (show nothing yet, to avoid a static-badge flash)
  //   null      → Supabase unreachable → fall back to the static catalog
  //   object    → loaded map certId → badge|null. Badge Manager is AUTHORITATIVE
  //               when reachable: a cert absent from the map has no assignment,
  //               so it shows NO emblem (not the static default).
  const [certEmblems, setCertEmblems] = React.useState(undefined);
  React.useEffect(() => {
    let alive = true;
    getCertEmblems().then((map) => {
      if (alive) setCertEmblems(map);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Learner testimonials for the social-proof row.
  //   undefined = loading AND we expect content (split layout + skeleton)
  //   []        = none (stable full-width promo)
  //   rows      = split layout with the carousel
  //
  // Bryan (prod, 2026-07-16): starting at `undefined` made EVERY visitor see
  // the split skeleton flash and collapse back to the full-width banner when
  // the table was empty. The initial state now comes from a localStorage hint
  // recording whether this browser last saw testimonials — no-testimonial
  // visitors get the stable banner with no flash, returning visitors with
  // testimonials get the split layout immediately.
  const [testimonials, setTestimonials] = React.useState(() => {
    try {
      return window.localStorage.getItem(TESTIMONIALS_HINT_KEY) === '1' ? undefined : [];
    } catch {
      return [];
    }
  });
  React.useEffect(() => {
    let alive = true;
    getPublicTestimonials().then((rows) => {
      if (!alive) return;
      setTestimonials(rows);
      try {
        if (rows.length > 0) window.localStorage.setItem(TESTIMONIALS_HINT_KEY, '1');
        else window.localStorage.removeItem(TESTIMONIALS_HINT_KEY);
      } catch {
        /* storage unavailable (private mode) — hint is best-effort */
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  // Resolve the product-badge override for a cert from the state above.
  const overrideFor = (examId) => {
    if (certEmblems === undefined) return null;          // loading → show nothing
    if (certEmblems === null) return undefined;          // DB down → static fallback
    return examId in certEmblems ? certEmblems[examId] : null; // reachable → authoritative
  };

  return (
    <div className="home">

      {/* ── Hero — brand wordmark stacked over avatar on the left, two-line
           headline + lede on the right ──────────────────────────────────── */}
      <section className="hero">
        <div className="hero-avatar">
          <h2 className="brand">
            PM PIXEL <span>v2.0</span>
          </h2>
          {/* Landing hero — PM Pixel's calm, welcoming idle pose. Eager-loaded
              because it's above the fold and brand-critical. Routed through
              CoachAvatar so it picks up the active theme's mascot in Phase 2. */}
          <div className="avatar-wrap">
            <CoachAvatar
              pose="idle"
              heightPx={400}
              loading="eager"
              decorative={false}
              className="avatar"
            />
          </div>
        </div>
        <div className="hero-content">
          <h1>
            Your Retro PM
            <span>Exam Coach</span>
          </h1>
          <p>
            <strong>Getting ready for a Project Management Certification?</strong>
          </p>
          {/* Founding Testers · Class of 2026 — historical recognition lives
              as a small mark floating over the lede's right side (replaces
              the retired promo card AND the interim recognition row). It
              quietly links to the preserved Founders Wall. */}
          <div className="lede-wrap">
            <p className="lede">
              PM Pixel provides realistic exam practice, personalized study plans,
              targeted testing and performance insights to help you study smarter,
              improve readiness and succeed on exam day — built by a PMI Authorized
              Trainer, PMBOK contributor and project management practitioner with
              more than 25 years of experience.
            </p>
            {onOpenFoundersWall && (
              <button
                type="button"
                className="founding-mark"
                data-testid="founding-mark"
                title="Founding Testers · Class of 2026 — meet the Founding Testers"
                aria-label="Founding Testers, Class of 2026 — meet the Founding Testers"
                onClick={onOpenFoundersWall}
              >
                <img
                  src="/themes/pm-pixel/mascot/PM_PIXEL_FOUNDING_2026.png"
                  alt=""
                  aria-hidden="true"
                />
              </button>
            )}
          </div>
          {/* Platform/Feature badge row — lives in the hero's right column,
              below the lede, in the open space beside the avatar. */}
          <PlatformBadgeRow />
        </div>
      </section>

      {/* ── Certifications — the page's primary CTAs ────────────────────── */}
      {/* Journey Activation onboarding (dark: journeyOnboardingEnabled).
          Sits between the hero and the cert grid; the grid below stays fully
          interactive — the journey surface assists, never blocks. */}
      {journeySlot}

      <section className="certifications">
        <h3>Choose Your Certification</h3>
        <div className="card-grid">
          {exams.map((exam) => {
            const visuals = EXAM_VISUALS[exam.examId] || { label: 'EXAM', cssClass: 'pmp' };
            const comingSoon = exam.comingSoon === true;
            return (
              <button
                key={exam.examId}
                type="button"
                data-testid={`exam-button-${exam.examId}`}
                className={`cert-card${comingSoon ? ' cert-card-coming-soon' : ''}`}
                style={buttonReset}
                onClick={comingSoon ? undefined : () => onSelectExam(exam.examId)}
                disabled={comingSoon}
                aria-disabled={comingSoon ? 'true' : undefined}
                title={comingSoon ? 'Coming soon — question bank is being built.' : undefined}
              >
                <CertIcon visuals={visuals} />
                <div className="cert-content">
                  <h4>{exam.displayName}</h4>
                  <p>{exam.fullDescription}</p>
                </div>
                {!comingSoon && <span className="cert-arrow" aria-hidden="true">→</span>}
                {/* Product + telemetry badges from the canonical badge system.
                    Placement, max rules and telemetry filtering are decided by
                    badgeService. The product emblem is driven by the admin Badge
                    Manager (authoritative when Supabase is reachable). A
                    "Coming Soon" status is now expressed as an emblem assigned
                    in the tool, not a hardcoded inline pill. */}
                <CardBadges
                  certId={exam.examId}
                  telemetryByCert={telemetryByCert}
                  productOverride={overrideFor(exam.examId)}
                />
              </button>
            );
          })}
        </div>

        {/* ── Success stories — admin-curated testimonials (migration 078),
             now a side-by-side pair (perView=2) filling the bottom row the
             retired Founders Wall promo used to share. Same data source and
             carousel machinery; a quiet two-pane skeleton reserves the space
             while loading. ──────────────────── */}
        {(testimonials === undefined || (Array.isArray(testimonials) && testimonials.length > 0)) && (
          <div className="stories-area">
            <h3 className="sr-only" id="testimonials-heading">
              Success stories from PM Pixel learners
            </h3>
            {testimonials === undefined && (
              <div className="stories-pair-skeleton" aria-hidden="true" data-testid="testimonials-skeleton">
                <div className="stories-col stories-skeleton" />
                <div className="stories-col stories-skeleton" />
              </div>
            )}
            {Array.isArray(testimonials) && testimonials.length > 0 && (
              <div data-testid="testimonials-section">
                <TestimonialsCarousel
                  testimonials={testimonials}
                  labelledBy="testimonials-heading"
                  perView={2}
                />
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Footer — Bryan's external links ──────────────────────────────── */}
      <footer className="footer">
        <p>
          Created by Bryan Campbell, PgMP, PMP, CPMAI, PMBOK contributor and
          Authorized PMI Trainer.
          <br />
          Explore Bryan&apos;s other work:
        </p>
        <div className="links">
          <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">
            Building Great Teams
          </a>
          <a href={SEVENCS_URL} target="_blank" rel="noopener noreferrer">
            7C&apos;s Consulting
          </a>
          <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
            LinkedIn
          </a>
        </div>
        <p className="marks-note">
          PMP, PMI-ACP, CPMAI, PMOCP and PMBOK are registered marks of the
          Project Management Institute, Inc. PM&nbsp;Pixel is an independent
          study tool and is not affiliated with or endorsed by PMI.
        </p>
      </footer>

    </div>
  );
}
